import {useState} from 'react';
import {CreatedMaintenanceRecordRequest} from '@di-shared/api/di/__generated';
import useOdometer from '@di-shared/features/mileage/useOdometer';
import {useSuspenseQuery} from '@tanstack/react-query';
import {getQueryKey} from '@shared/queryKeyMap';
import {diApi} from '@di-shared/api/di';
import environmentStore from '@di-shared/features/environment/environmentStore';
import {dateFormat} from '@shared/dateUtils';
import useUnitConverter from '@di-shared/utils/useUnitConverter';

function useSestExchangeRegisterForm(vin: string, sestCodes: CreatedMaintenanceRecordRequest.sestCode[]) {
  const puid = environmentStore.getState().puid;
  const {odometer} = useOdometer(vin);
  const {revertDistanceValue} = useUnitConverter();
  const {data: userRole} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.vehicle.getVehicle), vin, puid],
    queryFn: () => diApi.vehicle.getVehicle({vin, puid}),
    select: (data) => data.role.code,
  });

  const [enteredOdometer, setEnteredOdometer] = useState('');
  /**
   * 유효성 확인 예외 : <Case1>에 해당하는 차량은 유효성 체크 안 함
   * (CCS 미가입 or CCS 미지원 차량)
   */
  const isOdometerError = userRole === 1 ? false : +enteredOdometer > odometer;

  const today = new Date();
  const [exchangeDate, setExchangeDate] = useState<Date | null>(null);
  const isExchangeDateError = exchangeDate ? exchangeDate > today : false;

  function updateEnteredOdometer(updatedOdometer: string) {
    setEnteredOdometer(updatedOdometer);
  }

  function updateExchangeDate(updatedExchangeDate: Date) {
    setExchangeDate(updatedExchangeDate);
  }

  async function registerRecord() {
    if (!exchangeDate) {
      return;
    }
    /**
     * case1 유저의 경우 수신된 odometer 보다 입력주행거리가 더 클 수 있음 (case1이 아니면 불가능)
     * odometer 보다 입력한 값이 더 클때는 odometer도 갱신 해줘야함
     */
    return Promise.all([
      userRole === 1 &&
        odometer < +enteredOdometer &&
        diApi.maintenancesSestController.updateOdometer({
          vin,
          requestBody: {value: revertDistanceValue(+enteredOdometer)},
        }),
      diApi.maintenanceRecordController.createMaintenanceRecord({
        vin,
        requestBody: {
          records: sestCodes.map((sestCode) => ({
            vin,
            puid,
            replaceOdometer: revertDistanceValue(+enteredOdometer),
            replaceDate: dateFormat(exchangeDate, 'yyyyMMdd'),
            sestCode,
          })),
        },
      }),
    ]);
  }

  const isRegisterButtonDisabled = !enteredOdometer || !exchangeDate || isOdometerError || isExchangeDateError;

  const hasEngineOil = sestCodes.find((sestCode) => sestCode === CreatedMaintenanceRecordRequest.sestCode._01);
  //TODO: 브레이크 패드는 TBD
  const hasBrakePad = false;
  const hasBattery = sestCodes.find((sestCode) => sestCode === CreatedMaintenanceRecordRequest.sestCode._18);

  return {
    registerRecord,
    enteredOdometer,
    isOdometerError,
    isExchangeDateError,
    exchangeDate,
    updateEnteredOdometer,
    setExchangeDate,
    updateExchangeDate,
    isRegisterButtonDisabled,
    hasEngineOil,
    hasBattery,
    hasBrakePad,
  };
}

export default useSestExchangeRegisterForm;
